import React, { createContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';

// Define user interface
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  isAdmin?: boolean;
}

// Define the shape of our context
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

// Create context with default values
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
});

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      
      // Store token in localStorage
      localStorage.setItem('token', response.token);
      
      // Set user state
      setCurrentUser(response.user);
      
      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Remove token from localStorage
      localStorage.removeItem('token');
      
      // Clear user state
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Check if user is already logged in (has valid token)
  useEffect(() => {
    const checkAuthStatus = async () => {
      setLoading(true);
      
      try {
        const token = localStorage.getItem('token');
        console.log('Auth token present:', !!token); // Debug
        
        // If there's a token, try to get current user
        if (token) {
          const response = await authAPI.getCurrentUser();
          console.log('Current user response:', response); // Debug
          
          if (response && response.user) {
            setCurrentUser(response.user);
            console.log('User authenticated:', response.user); // Debug
          } else {
            console.error('Invalid auth response:', response);
            localStorage.removeItem('token');
            setCurrentUser(null);
          }
        }
      } catch (error) {
        // If token is invalid or expired, clear it
        console.error('Auth check error:', error);
        localStorage.removeItem('token');
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};